import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import BibliothequePlotly from '../BibliothequePlotly/BibliothequePlotly';
import './styles/index.scss';
import * as glossaire_type_de_graphe from '../../../../ressources/glossaires/glossaire_type_de_graphe';
import {
  LabelHoverCard,
  LabelEditDialog,
  PhaseEditDialog,
} from './LabelComponents';
import useLabelCrud from './hooks/useLabelCrud';
import usePhaseCrud from './hooks/usePhaseCrud';
import usePlotlyData from './hooks/usePlotlyData';
import usePlotlyEvents from './hooks/usePlotlyEvents';
import LabelToolbar from './components/LabelToolbar';
import CustomLegend from './components/CustomLegend';
import useIconOverlay from './hooks/useIconOverlay';
import { ICON_PATHS } from './utils/iconSvgPaths';
import { exportGraphImage } from './utils/exportGraphImage';

const LabelEventGraph = ({
  donnees = [],
  configuration = {},
  donneesGraphe,
  seuils = [],
  dimensionWidth,
  dimensionHeight,
  switchAfficherModifierIntervalle,
  viewobj_json_content = { displayModeBar: true },
  onLabelHover,
  onLabelUnhover,
  onPhaseClick,
  onAddLabel,
}) => {
  const isLabelMode =
    donneesGraphe?.config?.graph_type_id ===
    glossaire_type_de_graphe.graphe_x_t_label;

  const graphId = donneesGraphe?.config?.graph_id;
  // In non-label mode, pass null to prevent API calls inside the CRUD hooks.
  const labelGraphId = isLabelMode ? graphId : null;

  const graphCurves = useMemo(() => {
    if (donneesGraphe?.graphCurves?.length) return donneesGraphe.graphCurves;
    return donnees
      .filter((d) => d.id_sensor && d.position_in_sensor !== undefined)
      .map((d) => ({ formula: `#${d.id_sensor},${d.position_in_sensor}#` }));
  }, [donneesGraphe?.graphCurves, donnees]);

  const sensorIds = useMemo(() => {
    if (donneesGraphe?.id_sensor?.length) return donneesGraphe.id_sensor;
    const fromDonnees = [
      ...new Set(donnees.map((d) => d.id_sensor).filter(Boolean)),
    ];
    if (fromDonnees.length) return fromDonnees;
    return [
      ...new Set(
        (donneesGraphe?.graphCurves || [])
          .map((c) => {
            try {
              return Number((c.formula || '').match(/\d+/g)?.[0]);
            } catch {
              return null;
            }
          })
          .filter(Boolean)
      ),
    ];
  }, [donneesGraphe?.id_sensor, donneesGraphe?.graphCurves, donnees]);

  const dateOptions = useMemo(() => {
    const tArr = donnees[0]?.x || donnees[0]?.t || [];
    const yArr = donnees[0]?.y || [];
    const pkArr = donnees[0]?.pk || [];
    return tArr
      .map((ts, i) => ({ ts, y: yArr[i], pk: pkArr[i] ?? i }))
      .filter((_, i) => i % 2 === 0);
  }, [donnees]);

  const {
    localLabels,
    loadingAnnotations,
    editingLabel,
    setEditingLabel,
    creatingLabel,
    setCreatingLabel,
    labelError,
    setLabelError,
    hoveredLabel,
    setHoveredLabel,
    timerEtiquette,
    addLabel,
    handleAddLabelFromSignal,
    handleSaveLabel,
    handleRemoveLabel,
    handlePencilEdit,
  } = useLabelCrud(labelGraphId, sensorIds, graphCurves, dateOptions);

  const {
    localPhases,
    editingPhase,
    setEditingPhase,
    creatingPhase,
    setCreatingPhase,
    ajouterPhase,
    handleSavePhase,
    handleDeletePhase,
    handlePhaseClickInternal,
  } = usePhaseCrud(labelGraphId, sensorIds, donnees, dateOptions);

  const chartWidthPx = dimensionWidth ? Math.max(dimensionWidth - 320, 400) : 1000;
  // Hauteur de la bande labels en px : LABEL_DOMAIN (16%) de l'aire de tracé (hauteur - marges 60+60)
  const plotAreaH = (dimensionHeight || 600) - 120;
  const labelBandHeightPx = Math.max(0.16 * plotAreaH, 60);

  const { allTraces, annMeta, annotations, layout, sectionsRef, tRawRef, labelsRawRef, labelCategories, legendItems } =
    usePlotlyData(
      donnees,
      configuration,
      localLabels,
      localPhases,
      graphCurves,
      seuils,
      isLabelMode,
      chartWidthPx,
      labelBandHeightPx
    );

  const [hiddenGroups, setHiddenGroups] = useState(() => new Set());

  const visibleTraces = useMemo(() => {
    if (hiddenGroups.size === 0) return allTraces;
    return allTraces.map((t) => ({
      ...t,
      visible: t.legendgroup && hiddenGroups.has(t.legendgroup) ? false : true,
    }));
  }, [allTraces, hiddenGroups]);

  const toggleGroup = useCallback((id) => {
    setHiddenGroups((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const isolateGroup = useCallback((id) => {
    setHiddenGroups((prev) => {
      const allIds = legendItems.map((i) => i.id);
      const othersAllHidden = allIds.filter((i) => i !== id).every((i) => prev.has(i));
      return othersAllHidden ? new Set() : new Set(allIds.filter((i) => i !== id));
    });
  }, [legendItems]);

  const {
    activeLayout,
    handleRelayout,
    dragmode,
    handleHover,
    handleUnhover,
    handleClick,
    handleDoubleClick,
    handleClickAnnotation,
  } = usePlotlyEvents({
    isLabelMode,
    layout,
    annotations,
    labelsRawRef,
    sectionsRef,
    tRawRef,
    setHoveredLabel,
    timerEtiquette,
    handlePhaseClickInternal,
    onSignalClick: isLabelMode ? handleAddLabelFromSignal : onAddLabel,
    onLabelHover,
    onLabelUnhover,
    onPhaseClick,
    signalCount: donnees.length,
  });

  const containerRef = useRef(null);
  const iconOverlay = useIconOverlay(containerRef, annMeta, hiddenGroups);
  const [modebarNode, setModebarNode] = useState(null);

  useEffect(() => {
    if (!isLabelMode || !containerRef.current) return;
    const find = () => containerRef.current?.querySelector('.modebar');
    const node = find();
    if (node) { setModebarNode(node); return; }
    const observer = new MutationObserver(() => {
      const n = find();
      if (n) { setModebarNode(n); observer.disconnect(); }
    });
    observer.observe(containerRef.current, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [isLabelMode]);

  // Plotly does not update the cursor on fixedrange subplot drag rects when dragmode changes.
  // Force the label zone (data-subplot="xy2") to match the main plot cursor.
  useEffect(() => {
    if (!containerRef.current) return;
    const drags = containerRef.current.querySelectorAll('[data-subplot="xy2"]');
    if (!drags.length) return;
    const cursor = dragmode === 'pan' ? 'move' : 'crosshair';
    drags.forEach((el) => { el.style.cursor = cursor; });
  }, [dragmode]);

  const plotHeight = dimensionHeight;

  // La modebar Plotly fige le closure de son bouton custom au 1er render (config lue une seule fois).
  // Or les labels arrivent de façon asynchrone : on lit donc les données fraîches via une ref,
  // pour que l'export utilise toujours la dernière légende + les derniers icônes, pas ceux du 1er render.
  const exportRef = useRef({});
  exportRef.current = {
    // Descripteurs d'icônes en coordonnées données (positions px recalculées à l'export)
    iconData: annMeta
      .filter((ev) => ev.hasSvg)
      .map((ev) => ({
        xDate: ev.xDate,
        markerY: ev.markerY,
        color: ev.style.color,
        iconType: ev.iconType,
        legendGroup: ev.legendGroup,
      })),
    hiddenGroups,
    legendItems,
    title: configuration.title || donneesGraphe?.config?.title || 'graphe',
  };

  const handleExportImage = useCallback(() => {
    const gd = containerRef.current?.querySelector('.js-plotly-plot');
    const { iconData, hiddenGroups: hidden, legendItems: items, title } = exportRef.current;
    exportGraphImage({ gd, iconData, hiddenGroups: hidden, legendItems: items, iconSize: 22, fileName: title });
  }, []);

  // Labels + phases pour l'export CSV (ajoutés en sections séparées dans BibliothequePlotly)
  const csvLabels = useMemo(
    () => [
      ...(Array.isArray(configuration.labels) ? configuration.labels : []),
      ...localLabels,
    ],
    [configuration.labels, localLabels]
  );
  const csvPhases = useMemo(
    () => [
      ...(Array.isArray(configuration.sections) ? configuration.sections : []),
      ...localPhases,
    ],
    [configuration.sections, localPhases]
  );

  const plotly = (
    <BibliothequePlotly
      data={visibleTraces}
      layout={activeLayout}
      donneesGraphe={donneesGraphe}
      dimensionWidth={dimensionWidth}
      dimensionHeight={plotHeight}
      viewobj_json_content={viewobj_json_content}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onHover={handleHover}
      onUnhover={handleUnhover}
      onRelayout={handleRelayout}
      onClickAnnotation={handleClickAnnotation}
      onExportImage={isLabelMode ? handleExportImage : undefined}
      csvLabels={isLabelMode ? csvLabels : undefined}
      csvPhases={isLabelMode ? csvPhases : undefined}
      ouvrirPopupModifierIntervalle={switchAfficherModifierIntervalle}
    />
  );

  const toolbar = (
    <LabelToolbar
      loading={loadingAnnotations}
      onAddLabel={addLabel}
      onAddPhase={ajouterPhase}
    />
  );

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      {plotly}

      <CustomLegend
        items={legendItems}
        hiddenGroups={hiddenGroups}
        onToggle={toggleGroup}
        onIsolate={isolateGroup}
      />

      {iconOverlay.length > 0 && (
        <svg
          className="leg-icon-overlay"
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'visible' }}
        >
          {iconOverlay.map(({ xPx, yPx, color, iconType, key }) => {
            const entry = ICON_PATHS[iconType];
            if (!entry) return null;
            const size = 22;
            return (
              <g key={key} data-key={key} transform={`translate(${xPx - size / 2}, ${yPx - size / 2})`}>
                <svg width={size} height={size} viewBox={entry.viewBox}>
                  {entry.d.map((d, di) => (
                    <path key={di} d={d} fill={color} />
                  ))}
                </svg>
              </g>
            );
          })}
        </svg>
      )}

      {isLabelMode && (modebarNode
        ? ReactDOM.createPortal(toolbar, modebarNode)
        : (
          <div style={{ position: 'absolute', top: 40, right: 8, zIndex: 3000, pointerEvents: 'none' }}>
            {toolbar}
          </div>
        )
      )}

      {isLabelMode && hoveredLabel?.label && !editingLabel && (
        <LabelHoverCard
          labelData={hoveredLabel.label}
          x={hoveredLabel.x}
          y={hoveredLabel.y}
          onEdit={handlePencilEdit}
          onRemove={handleRemoveLabel}
          onMouseEnter={() => clearTimeout(timerEtiquette.current)}
          onMouseLeave={() => {
            timerEtiquette.current = setTimeout(
              () => setHoveredLabel(null),
              300
            );
          }}
        />
      )}

      {editingLabel && (
        <LabelEditDialog
          label={editingLabel}
          dateOptions={dateOptions}
          sensorsPositions={graphCurves}
          data={donnees}
          error={labelError}
          onSave={handleSaveLabel}
          onCancel={() => {
            setEditingLabel(null);
            setLabelError(null);
          }}
        />
      )}

      {creatingLabel && (
        <LabelEditDialog
          label={creatingLabel}
          dateOptions={dateOptions}
          sensorsPositions={graphCurves}
          data={donnees}
          error={labelError}
          onSave={handleSaveLabel}
          onCancel={() => {
            setCreatingLabel(null);
            setLabelError(null);
          }}
        />
      )}

      {editingPhase && (
        <PhaseEditDialog
          phase={editingPhase}
          onSave={handleSavePhase}
          onCancel={() => setEditingPhase(null)}
          onDelete={handleDeletePhase}
        />
      )}

      {creatingPhase && (
        <PhaseEditDialog
          phase={creatingPhase}
          onSave={handleSavePhase}
          onCancel={() => setCreatingPhase(null)}
        />
      )}
    </div>
  );
};

export default LabelEventGraph;
